import { queryOptions, useQuery } from "@tanstack/react-query";
import { httpPostGraphql } from "shared/api/httpClient";
import { useIpawsAlertFormContext } from "shared/components/ipaws/alert/context";
import { useFormState } from "shared/hooks/useFormState";
import { uniqueBy } from "shared/utils/fns";
import { zx } from "shared/utils/zx";
import { graphql } from "src/gql";
import { IpawsChannel } from "src/gql/graphql";
import z from "zod";

export function useIpawsAlertFormCertificate() {
    const form = useIpawsAlertFormContext();
    const [certId] = useFormState(form, "certId");

    return useQuery(queries.certificate(certId));
}

export function useIpawsAlertFormAllowedEventCodes() {
    const form = useIpawsAlertFormContext();
    const [certId] = useFormState(form, "certId");
    const [channels] = useFormState(form, "channels");

    return useQuery(queries.eventCodes(certId, channels));
}

export function useIpawsAlertFormAllowedGeoCodes() {
    const form = useIpawsAlertFormContext();
    const [certId] = useFormState(form, "certId");
    const [channels] = useFormState(form, "channels");

    return useQuery(queries.geoCodes(certId, channels));
}

const queries = {
    geoCodes: (certId: string | undefined, channels: IpawsChannel[]) =>
        queryOptions({
            queryKey: ["ipaws-alert-form-configuration", "geo-codes", certId, channels],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query IpawsAlertFormConfigurationGeoCodes($certId: UUID!, $channels: [IpawsChannel!]!) {
                            ipawsCertificate(id: $certId) {
                                geoCodes(channels: $channels) {
                                    code
                                    boundary
                                }
                            }
                        }
                    `),
                    { certId: certId!, channels },
                ),
            enabled: !!certId,
            placeholderData: { ipawsCertificate: { geoCodes: [] } },
            select: (incoming) =>
                incoming.ipawsCertificate.geoCodes.filter(uniqueBy("code")).map((it) =>
                    z
                        .object({
                            code: z.string(),
                            boundary: zx.geo.coerce.feature<GeoJSON.Polygon>(zx.geo.polygon()),
                        })
                        .parse(it),
                ),
        }),
    eventCodes: (certId: string | undefined, channels: IpawsChannel[]) =>
        queryOptions({
            queryKey: ["ipaws-alert-form-configuration", "event-codes", certId, channels],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query IpawsAlertFormConfigurationEventCodes($certId: UUID!, $channels: [IpawsChannel!]!) {
                            ipawsCertificate(id: $certId) {
                                eventCodes(channels: $channels) {
                                    id
                                    description
                                }
                            }
                        }
                    `),
                    { certId: certId!, channels },
                ),
            enabled: !!certId,
            placeholderData: { ipawsCertificate: { eventCodes: [] } },
            select: (incoming) => incoming.ipawsCertificate.eventCodes.filter(uniqueBy("id")),
        }),
    certificate: (id?: string) =>
        queryOptions({
            queryKey: ["ipaws-alert-form-profile", "certificate", id],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query IpawsAlertFormProfileCertificate($id: UUID!) {
                            ipawsCertificate(id: $id) {
                                id
                                name
                                type
                                active
                                channels {
                                    channel
                                    userEnabled
                                }
                            }
                        }
                    `),
                    { id: id! },
                ),
            enabled: !!id,
        }),
};
